import React, { useState, useEffect } from 'react';
import { ShieldCheck, Award, CheckCircle2 } from 'lucide-react';
import { CertificationConfig } from '../types';
import {
  getStoredCertifications,
  subscribeToCertifications,
  syncCertificationsWithServer,
} from '../utils/certifications';

interface CertificationBadgesProps {
  className?: string;
  variant?: 'dark' | 'light';
  showTitle?: boolean;
}

export const CertificationBadges: React.FC<CertificationBadgesProps> = ({
  className = '',
  variant = 'dark',
  showTitle = true,
}) => {
  const isDark = variant === 'dark';
  const [certConfigs, setCertConfigs] = useState<CertificationConfig[]>(getStoredCertifications());

  useEffect(() => {
    // Initial sync with server
    syncCertificationsWithServer().then((certs) => {
      setCertConfigs(certs);
    });

    // Subscribe to live changes
    const unsubscribe = subscribeToCertifications((certs) => {
      setCertConfigs(certs);
    });

    return unsubscribe;
  }, []);

  // Built-in crisp vector icons (fallback when no custom photo is uploaded)
  const defaultVectorIcons: Record<string, React.ReactNode> = {
    'saglik-bakanligi': (
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="100" r="98" fill="#FFFFFF" />
        <circle cx="100" cy="100" r="95" stroke="#E30A17" strokeWidth="4.5" strokeDasharray="3 3.5" />
        <circle cx="100" cy="100" r="91" stroke="#E30A17" strokeWidth="1" />
        {[0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5, 180, 202.5, 225, 247.5, 270, 292.5, 315, 337.5].map((deg, i) => (
          <g key={i} transform={`rotate(${deg} 100 100)`}>
            <polygon
              points="100,14 102.2,20.5 108.5,20.5 103.5,24.5 105.5,31 100,27 94.5,31 96.5,24.5 91.5,20.5 97.8,20.5"
              fill="#E30A17"
            />
          </g>
        ))}
        <circle cx="100" cy="100" r="70" stroke="#E30A17" strokeWidth="1.2" />
        <circle cx="100" cy="100" r="54" stroke="#E30A17" strokeWidth="1.8" />
        <path id="badge-sb-arc" d="M 36 128 A 62 62 0 1 1 164 128" fill="none" />
        <text fill="#E30A17" fontSize="10.5" fontWeight="900" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="2.2">
          <textPath href="#badge-sb-arc" startOffset="50%" textAnchor="middle">
            T.C. SAĞLIK BAKANLIĞI
          </textPath>
        </text>
        <circle cx="100" cy="158" r="2.8" fill="#E30A17" />
        <g transform="translate(100, 100)">
          <g transform="translate(0, -28)">
            <polygon points="0,-8 1.5,-4 5.5,-4 2.2,-1.5 3.5,2.5 0,0 -3.5,2.5 -2.2,-1.5 -5.5,-4 -1.5,-4" fill="#E30A17" />
            <path d="M 0 -1 C 5 -1 8 4 8 8 C 8 13 4 17 0 17 C -4 17 -8 13 -8 8 C -8 4 -5 -1 0 -1 Z M 0 2.5 C 2.5 2.5 5 5 5 8 C 5 11 2.5 13.5 0 13.5 C -2.5 13.5 -5 11 -5 8 C -5 5 -2.5 2.5 0 2.5 Z" fill="#E30A17" />
          </g>
          <line x1="0" y1="-8" x2="0" y2="44" stroke="#E30A17" strokeWidth="2.8" strokeLinecap="round" />
          <circle cx="0" cy="-9" r="4.5" fill="#E30A17" />
          <polygon points="-1.5,43 1.5,43 0,48" fill="#E30A17" />
          <g fill="#E30A17">
            <path d="M -5 -7 C -18 -10 -40 -6 -43 -5 C -40 -2 -25 0 -12 1 Z" />
            <path d="M -6 -2 C -20 -3 -35 1 -37 3 C -34 5 -22 6 -10 4 Z" />
            <path d="M -6 3 C -18 3 -30 8 -32 10 C -28 11 -17 10 -8 7 Z" />
            <path d="M -6 7 C -15 8 -24 13 -25 14 C -22 15 -13 13 -6 10 Z" />
          </g>
          <g fill="#E30A17" transform="scale(-1, 1)">
            <path d="M -5 -7 C -18 -10 -40 -6 -43 -5 C -40 -2 -25 0 -12 1 Z" />
            <path d="M -6 -2 C -20 -3 -35 1 -37 3 C -34 5 -22 6 -10 4 Z" />
            <path d="M -6 3 C -18 3 -30 8 -32 10 C -28 11 -17 10 -8 7 Z" />
            <path d="M -6 7 C -15 8 -24 13 -25 14 C -22 15 -13 13 -6 10 Z" />
          </g>
          <path
            d="M -7 0 C -16 5 -10 18 0 13 C 10 8 16 21 0 26 C -14 31 -8 40 0 38"
            fill="none"
            stroke="#E30A17"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M 7 0 C 16 5 10 18 0 13 C -10 8 -16 21 0 26 C 14 31 8 40 0 38"
            fill="none"
            stroke="#E30A17"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <circle cx="-8" cy="0" r="2.2" fill="#E30A17" />
          <circle cx="8" cy="0" r="2.2" fill="#E30A17" />
        </g>
      </svg>
    ),
    'ce-medical': (
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="100" r="96" fill="#0B192C" />
        <circle cx="100" cy="100" r="92" stroke="#1E40AF" strokeWidth="3" />
        <circle cx="100" cy="100" r="88" stroke="#3B82F6" strokeWidth="1" strokeDasharray="3 3" />
        {[140, 160, 180, 200, 220].map((deg, i) => (
          <g key={i} transform={`rotate(${deg} 100 100)`}>
            <polygon
              points="100,20 101.5,24 105.5,24 102.5,26.5 103.5,30.5 100,28 96.5,30.5 97.5,26.5 94.5,24 98.5,24"
              fill="#FBBF24"
            />
          </g>
        ))}
        <g transform="translate(100, 92)">
          <path
            d="M -12 -38 C -46 -38 -50 38 -12 38 C -7 38 0 34 0 34 L -4 24 C -4 24 -9 27 -14 27 C -34 27 -32 -27 -14 -27 C -9 -27 -4 -24 -4 -24 L 0 -34 C 0 -34 -7 -38 -12 -38 Z"
            fill="#FFFFFF"
          />
          <path
            d="M 32 -38 C 12 -38 6 38 32 38 C 37 38 44 34 44 34 L 40 24 C 40 24 35 27 30 27 C 20 27 20 11 20 11 L 42 11 L 42 -2 L 20 -2 C 20 -2 20 -27 30 -27 C 35 -27 40 -24 40 -24 L 44 -34 C 44 -34 37 -38 32 -38 Z"
            fill="#FFFFFF"
          />
        </g>
        <rect x="36" y="142" width="128" height="26" rx="6" fill="#1D4ED8" stroke="#60A5FA" strokeWidth="1.5" />
        <text x="100" y="159" textAnchor="middle" fill="#FFFFFF" fontSize="12" fontWeight="900" fontFamily="system-ui, sans-serif" letterSpacing="1.2">
          MEDICAL 2797
        </text>
      </svg>
    ),
    'tuv-certified': (
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polygon
          points="60,10 140,10 190,60 190,140 140,190 60,190 10,140 10,60"
          fill="#0284C7"
          stroke="#38BDF8"
          strokeWidth="3.5"
        />
        <polygon
          points="64,18 136,18 182,64 182,136 136,182 64,182 18,136 18,64"
          fill="#0369A1"
          stroke="#BAE6FD"
          strokeWidth="1.5"
        />
        <text x="100" y="86" textAnchor="middle" fill="#FFFFFF" fontSize="38" fontWeight="900" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="2">
          TÜV
        </text>
        <line x1="38" y1="102" x2="162" y2="102" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" />
        <text x="100" y="124" textAnchor="middle" fill="#E0F2FE" fontSize="13.5" fontWeight="900" fontFamily="system-ui, sans-serif" letterSpacing="2.5">
          CERTIFIED
        </text>
        <rect x="42" y="138" width="116" height="24" rx="12" fill="#0C4A6E" stroke="#7DD3FC" strokeWidth="1.5" />
        <text x="100" y="154" textAnchor="middle" fill="#FFFFFF" fontSize="11" fontWeight="800" fontFamily="system-ui, sans-serif" letterSpacing="1">
          ISO 13485:2016
        </text>
      </svg>
    ),
    'mdr-regulation': (
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M 100 12 L 180 38 C 180 125 100 188 100 188 C 100 188 20 125 20 38 Z"
          fill="#312E81"
          stroke="#6366F1"
          strokeWidth="4"
        />
        <path
          d="M 100 24 L 168 46 C 168 118 100 172 100 172 C 100 172 32 118 32 46 Z"
          fill="#1E1B4B"
          stroke="#818CF8"
          strokeWidth="1.5"
        />
        <g transform="translate(100, 52)">
          <rect x="-4" y="-12" width="8" height="24" rx="2" fill="#F43F5E" />
          <rect x="-12" y="-4" width="24" height="8" rx="2" fill="#F43F5E" />
        </g>
        <text x="100" y="98" textAnchor="middle" fill="#FFFFFF" fontSize="30" fontWeight="900" fontFamily="system-ui, sans-serif" letterSpacing="2">
          MDR
        </text>
        <line x1="50" y1="110" x2="150" y2="110" stroke="#818CF8" strokeWidth="2" strokeLinecap="round" />
        <text x="100" y="128" textAnchor="middle" fill="#C7D2FE" fontSize="12" fontWeight="800" fontFamily="system-ui, sans-serif" letterSpacing="1">
          EU 2017/745
        </text>
        <text x="100" y="146" textAnchor="middle" fill="#38BDF8" fontSize="10.5" fontWeight="900" fontFamily="system-ui, sans-serif" letterSpacing="1.5">
          COMPLIANT
        </text>
      </svg>
    ),
    'emc-laser': (
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="100" r="94" fill="#042F2E" stroke="#0D9488" strokeWidth="3.5" />
        <circle cx="100" cy="100" r="88" stroke="#2DD4BF" strokeWidth="1" strokeDasharray="3 3" />
        <path d="M 40 85 A 64 64 0 0 1 160 85" stroke="#14B8A6" strokeWidth="3" strokeLinecap="round" fill="none" />
        <path d="M 52 95 A 50 50 0 0 1 148 95" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M 64 105 A 36 36 0 0 1 136 105" stroke="#5EEAD4" strokeWidth="1.5" strokeDasharray="2 2" fill="none" />
        <polygon
          points="106,42 86,90 102,90 92,142 120,86 102,86"
          fill="#FACC15"
          stroke="#FEF08A"
          strokeWidth="1.5"
        />
        <rect x="34" y="144" width="132" height="25" rx="6" fill="#0F766E" stroke="#5EEAD4" strokeWidth="1.5" />
        <text x="100" y="161" textAnchor="middle" fill="#FFFFFF" fontSize="11.5" fontWeight="900" fontFamily="system-ui, sans-serif" letterSpacing="1.2">
          EMC & LASER TESTED
        </text>
      </svg>
    ),
  };

  const styleConfigs: Record<string, { accentColor: string; badgeClass: string }> = {
    'saglik-bakanligi': {
      accentColor: 'from-rose-500/20 to-red-600/20',
      badgeClass: 'bg-rose-950/80 text-rose-300 border-rose-800/60',
    },
    'ce-medical': {
      accentColor: 'from-blue-500/20 to-indigo-600/20',
      badgeClass: 'bg-blue-950/80 text-blue-300 border-blue-800/60',
    },
    'tuv-certified': {
      accentColor: 'from-sky-500/20 to-blue-600/20',
      badgeClass: 'bg-sky-950/80 text-sky-300 border-sky-800/60',
    },
    'mdr-regulation': {
      accentColor: 'from-indigo-500/20 to-violet-600/20',
      badgeClass: 'bg-indigo-950/80 text-indigo-300 border-indigo-800/60',
    },
    'emc-laser': {
      accentColor: 'from-teal-500/20 to-emerald-600/20',
      badgeClass: 'bg-teal-950/80 text-teal-300 border-teal-800/60',
    },
  };

  const visibleCerts = certConfigs.filter((c) => c.enabled !== false);

  return (
    <div className={`w-full ${className}`}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-5 pb-3.5 border-b border-slate-800/80">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-lg bg-amber-500/10 border border-amber-400/30 flex items-center justify-center text-amber-400 shrink-0">
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white tracking-wide">
                  Medikal Kalite & Resmi Uygunluk Belgeleri
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-semibold text-emerald-300">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  Doğrulanmış Klinik
                </span>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-slate-300 font-medium">
            Tüm cihazlar T.C. Sağlık Bakanlığı onaylı ve medikal CE/TÜV standartlarındadır.
          </div>
        </div>
      )}

      {/* 5 Regulatory & Quality Badges Grid - Horizontal Card Layout (Logo Left, Text Right) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {visibleCerts.map((cert) => {
          const style = styleConfigs[cert.id] || {
            accentColor: 'from-slate-500/20 to-slate-600/20',
            badgeClass: 'bg-slate-900 text-slate-300 border-slate-700',
          };
          const hasCustomImage = Boolean(cert.customImage);

          return (
            <div
              key={cert.id}
              className={`group relative flex items-center gap-3 p-2.5 sm:p-3 rounded-xl transition-all duration-200 ${
                isDark
                  ? 'bg-slate-900/80 hover:bg-slate-850 border border-slate-800 hover:border-slate-700 shadow-sm'
                  : 'bg-white hover:bg-slate-50 border border-slate-200/90 shadow-2xs'
              }`}
            >
              {/* Logo Emblem Container on LEFT */}
              <div className="w-14 h-14 rounded-xl bg-white p-1.5 flex items-center justify-center shrink-0 border border-slate-200/90 shadow-xs group-hover:scale-105 transition-transform duration-200 overflow-hidden ring-1 ring-slate-100">
                <div className="w-full h-full flex items-center justify-center">
                  {hasCustomImage ? (
                    <img
                      src={cert.customImage ? `${cert.customImage}${cert.customImage.includes('?') ? '&' : '?'}w=112` : ''}
                      alt={cert.title}
                      width={56}
                      height={56}
                      decoding="async"
                      className="w-full h-full object-contain filter drop-shadow-2xs"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const fallbackElem = target.parentElement?.querySelector('.cert-fallback-icon');
                        if (fallbackElem) {
                          (fallbackElem as HTMLElement).style.display = 'flex';
                        }
                      }}
                    />
                  ) : (
                    defaultVectorIcons[cert.id] || <ShieldCheck className="w-7 h-7 text-rose-500" />
                  )}
                  {hasCustomImage && (
                    <div className="cert-fallback-icon hidden w-full h-full items-center justify-center">
                      {defaultVectorIcons[cert.id] || <ShieldCheck className="w-7 h-7 text-rose-500" />}
                    </div>
                  )}
                </div>
              </div>

              {/* Text Information on RIGHT */}
              <div className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded text-[8.5px] font-extrabold uppercase tracking-wider border ${style.badgeClass}`}
                  >
                    {cert.badgeText}
                  </span>
                </div>

                <div className={`text-xs font-bold leading-tight mt-1 truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {cert.title}
                </div>

                <p className={`text-[10px] mt-0.5 leading-snug font-medium truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  {cert.description}
                </p>

                <div className="mt-1 pt-1 border-t border-slate-800/60">
                  <span className={`text-[9px] font-mono block truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {cert.subCode}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
